import { Muestra } from '../types/muestra';
import { EstadoMuestra } from '../types';
import httpClient from './httpClient';
import { debugApiCall } from '../utils/debugAuth';
import { MAX_MUESTRAS_CARGA } from '../constants/muestras';

// Interfaces
export interface MuestrasParams {
  page?: number;
  limit?: number;
  estado?: EstadoMuestra;
  search?: string;
  fechaInicio?: Date;
  fechaFin?: Date;
  pacienteId?: string;
  examenId?: string;
}

// Service functions
export const fetchMuestras = async (params?: MuestrasParams) => {
  try {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          if (value instanceof Date) {
            queryParams.append(key, value.toISOString());
          } else {
            queryParams.append(key, String(value));
          }
        }
      });
    }

    const queryString = queryParams.toString();
    const url = `/api/muestras?optimized=true&${queryString}`;

    // Debug the API call
    await debugApiCall(url);

    // Use optimized endpoint for better performance
    const response = await httpClient.get(url);
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return {
        data: [],
        pagination: {
          total: 0,
          totalPages: 0,
          currentPage: params?.page || 1,
          limit: params?.limit ?? MAX_MUESTRAS_CARGA
        }
      };
    }

    // Enhanced error logging for better debugging
    console.error('Error en fetchMuestras:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      url: error.config?.url,
      headers: error.config?.headers,
      message: error.message
    });

    // If it's a 400 error with tenant issues, try the fallback non-optimized endpoint
    if (error.response?.status === 400 &&
      (error.response?.data?.message?.includes('tenant') ||
        error.response?.data?.error?.includes('Tenant'))) {
      console.warn('⚠️ Fallback: Intentando endpoint no optimizado debido a problemas de tenant');
      try {
        // Rebuild query params for fallback
        const fallbackParams = new URLSearchParams();
        if (params) {
          Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined) {
              if (value instanceof Date) {
                fallbackParams.append(key, value.toISOString());
              } else {
                fallbackParams.append(key, String(value));
              }
            }
          });
        }
        const fallbackUrl = `/api/muestras?${fallbackParams.toString()}`;
        await debugApiCall(fallbackUrl);
        const fallbackResponse = await httpClient.get(fallbackUrl);
        return fallbackResponse.data;
      } catch (fallbackError: any) {
        console.error('Error en endpoint de fallback:', fallbackError);
        throw error; // Throw original error, not fallback error
      }
    }

    throw error;
  }
};

export const crearMuestrasDesdeFactura = async (facturaId: string): Promise<Muestra[]> => {
  try {
    const response = await httpClient.post(`/api/muestras/factura/${facturaId}`);
    const muestras = response.data;
    // ...existing code...
    // Sincronizar cada muestra creada
    if (Array.isArray(muestras.muestras)) {
      for (const muestra of muestras.muestras) {
        try {
          // ...existing code...
          await syncMuestra(muestra);
        } catch (syncError) {
          console.error('Error sincronizando muestra:', syncError);
        }
      }
    }
    return muestras;
  } catch (error) {
    console.error('Error en crearMuestrasDesdeFactura:', error);
    throw error;
  }
};
export const syncMuestra = async (muestra: Partial<Muestra>) => {
  try {
    const syncUrl = import.meta.env.VITE_SYNC_MUESTRAS_URL;
    if (!syncUrl) {
      console.warn('No se ejecuta syncMuestra: VITE_SYNC_MUESTRAS_URL no está definida');
      return null;
    }
    const response = await httpClient.post(syncUrl, muestra);
    return response.data;
  } catch (error) {
    console.error('Error sincronizando muestra:', error);
    throw error;
  }
};
export const getMuestra = async (id: string): Promise<Muestra> => {
  try {
    if (!id) {
      throw new Error('ID de muestra es requerido');
    }

    const response = await httpClient.get(`/api/muestras/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error en getMuestra:', error);
    throw error;
  }
};

export const updateMuestraEstado = async (id: string, estado: EstadoMuestra): Promise<Muestra> => {
  try {
    if (!id || !estado) {
      throw new Error('ID de muestra y estado son requeridos');
    }

    // Use optimized endpoint for better performance
    const response = await httpClient.patch(`/api/muestras/${id}/estado?optimized=true`, { estado });
    const muestra = response.data;

    // No crear resultado automáticamente aquí - dejar que el frontend lo maneje
    // para evitar conflictos de duplicación

    return muestra;
  } catch (error) {
    console.error('Error en updateMuestraEstado:', error);
    throw error;
  }
};

export const getMuestrasPorCodigo = async (codigo: string): Promise<Muestra[]> => {
  try {
    if (!codigo) {
      throw new Error('Código de muestra es requerido');
    }

    // Use optimized endpoint for better performance
    const response = await httpClient.get(`/api/muestras/codigo/${codigo}`);
    return response.data;
  } catch (error) {
    console.error('Error en getMuestrasPorCodigo:', error);
    throw error;
  }
};

export const eliminarMuestra = async (id: string): Promise<void> => {
  try {
    if (!id) {
      throw new Error('ID de muestra es requerido');
    }

    await httpClient.delete(`/api/muestras/${id}`);
  } catch (error) {
    console.error('Error en eliminarMuestra:', error);
    throw error;
  }
};

export const actualizarMuestra = async (id: string, datos: Partial<Muestra>): Promise<Muestra> => {
  try {
    if (!id) {
      throw new Error('ID de muestra es requerido');
    }

    const response = await httpClient.put(`/api/muestras/${id}`, datos);
    return response.data;
  } catch (error) {
    console.error('Error en actualizarMuestra:', error);
    throw error;
  }
};

export const eliminarMuestrasPorFactura = async (facturaId: string): Promise<void> => {
  try {
    if (!facturaId) {
      throw new Error('ID de factura es requerido');
    }

    await httpClient.delete(`/api/muestras/factura/${facturaId}`);
  } catch (error) {
    console.error('Error en eliminarMuestrasPorFactura:', error);
    throw error;
  }
};

// New optimized functions for better performance
export const getMuestrasPendientes = async (search?: string, limit: number = 100): Promise<Muestra[]> => {
  try {
    const queryParams = new URLSearchParams();
    if (search) queryParams.append('search', search);
    if (limit) queryParams.append('limit', limit.toString());

    const response = await httpClient.get(`/api/muestras/pendientes?optimized=true&${queryParams.toString()}`);
    return response.data.data || response.data;
  } catch (error) {
    console.error('Error en getMuestrasPendientes:', error);
    throw error;
  }
};

export const getMuestrasPorFactura = async (facturaId: string): Promise<Muestra[]> => {
  try {
    if (!facturaId) {
      throw new Error('ID de factura es requerido');
    }

    const response = await httpClient.get(`/api/muestras/factura/${facturaId}?optimized=true`);
    return response.data.data || response.data;
  } catch (error) {
    console.error('Error en getMuestrasPorFactura:', error);
    throw error;
  }
};

interface EstadisticasMuestras {
  total: number;
  pendientes: number;
  tomadas: number;
  [key: string]: unknown;
}

export const getEstadisticasMuestras = async (): Promise<EstadisticasMuestras> => {
  try {
    const response = await httpClient.get('/api/muestras/estadisticas?optimized=true');
    return response.data.data || response.data;
  } catch (error) {
    console.error('Error en getEstadisticasMuestras:', error);
    throw error;
  }
};

interface SearchMuestrasResult {
  data: Muestra[];
  pagination: {
    total: number;
    totalPages: number;
    currentPage: number;
    limit: number;
  };
}

export const searchMuestrasAvanzada = async (filters: {
  estado?: EstadoMuestra;
  prioridad?: string;
  fechaInicio?: Date;
  fechaFin?: Date;
  search?: string;
  page?: number;
  limit?: number;
}): Promise<SearchMuestrasResult> => {
  try {
    const queryParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined) {
        if (value instanceof Date) {
          queryParams.append(key, value.toISOString());
        } else {
          queryParams.append(key, String(value));
        }
      }
    });

    const response = await httpClient.get(`/api/muestras/search?optimized=true&${queryParams.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error en searchMuestrasAvanzada:', error);
    throw error;
  }
};